import { ClipboardList, FlaskConical, Sparkles } from 'lucide-react';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { formatValue } from '@/lib/format';
import { assignmentStatusLabel, type PatientResult, type PsyAssignment } from '@/lib/psyWorkspace';
import { resultSummary } from '@/components/psy/PsyResultsTable';
import type { PsyConclusion } from '@/components/medic/patientCardData';

export type PsyDetailEntry =
    | { kind: 'result'; result: PatientResult }
    | { kind: 'assignment'; assignment: PsyAssignment; methodName: string }
    | { kind: 'conclusion'; conclusion: PsyConclusion };

interface Props {
    entry: PsyDetailEntry | null;
    onClose: () => void;
}

function row(label: string, value: string) {
    return (
        <div className="flex gap-2 text-xs">
            <span className="w-32 shrink-0 text-muted-foreground">{label}</span>
            <span className="min-w-0 whitespace-pre-wrap break-words">{value || '—'}</span>
        </div>
    );
}

export function PatientCardPsyDetailDialog({ entry, onClose }: Props) {
    const title = entry?.kind === 'result'
        ? `Результат теста: ${entry.result.methodName}`
        : entry?.kind === 'assignment'
            ? `Назначение теста: ${entry.methodName}`
            : entry?.kind === 'conclusion'
                ? 'Заключение психолога'
                : 'Психология';
    return (
        <UniversalWindow
            open={!!entry}
            onClose={onClose}
            title={title}
            width="w-[min(760px,94vw)]"
            height="h-[min(70vh,88vh)]"
        >
            <div className="space-y-3 p-4">
                {entry?.kind === 'result' && (
                    <>
                        <div className="flex items-center gap-2 text-sm font-medium">
                            <FlaskConical className="h-4 w-4 text-primary" />
                            {entry.result.methodName}
                        </div>
                        <div className="space-y-1.5 rounded-lg border bg-card p-3">
                            {row('Дата', formatValue(entry.result.createdAt, '', 'timestamp'))}
                            {row('Балл', resultSummary(entry.result) || entry.result.score || '—')}
                            {row('Порог', entry.result.passed ? 'пройден' : 'не пройден')}
                        </div>
                        <div className="rounded-lg border bg-card p-3">
                            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Заключение</div>
                            <div className="mt-1 whitespace-pre-wrap break-words text-xs">
                                {entry.result.conclusion || 'Заключение не заполнено.'}
                            </div>
                        </div>
                    </>
                )}

                {entry?.kind === 'assignment' && (
                    <>
                        <div className="flex items-center gap-2 text-sm font-medium">
                            <ClipboardList className="h-4 w-4 text-primary" />
                            {entry.methodName}
                        </div>
                        <div className="space-y-1.5 rounded-lg border bg-card p-3">
                            {row('Назначено', formatValue(entry.assignment.assignedDate, '', 'timestamp'))}
                            {row('Статус', assignmentStatusLabel(entry.assignment.status))}
                            {row('Код методики', entry.assignment.methodCode)}
                        </div>
                        <div className="rounded-lg border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                            Результат по этому назначению ещё не получен. Прохождение методики и выдачу ссылки выполняет специалист.
                        </div>
                    </>
                )}

                {entry?.kind === 'conclusion' && (
                    <>
                        <div className="flex items-center gap-2 text-sm font-medium">
                            <Sparkles className="h-4 w-4 text-primary" />
                            Заключение психолога
                        </div>
                        <div className="space-y-1.5 rounded-lg border bg-card p-3">
                            {row('Дата', formatValue(entry.conclusion.date, '', 'date'))}
                            {row('Код МКБ', entry.conclusion.icdCode)}
                        </div>
                        <div className="rounded-lg border bg-card p-3">
                            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Заключение</div>
                            <div className="mt-1 whitespace-pre-wrap break-words text-xs">
                                {entry.conclusion.text || 'Текст заключения не заполнен.'}
                            </div>
                        </div>
                        <div className="rounded-lg border bg-card p-3">
                            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Рекомендации</div>
                            <div className="mt-1 whitespace-pre-wrap break-words text-xs">
                                {entry.conclusion.recommendations || 'Рекомендации не заполнены.'}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </UniversalWindow>
    );
}
